import { useMemo } from "react";
import type { MouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Card, Flex, List, SimpleGrid, Stack, Text, Title } from "@mantine/core";
import {
  IconArrowRight,
  IconBolt,
  IconBuildingFactory2,
  IconCar,
  IconHeartRateMonitor,
  IconLeaf,
  IconPlaneTilt,
  IconSparkles,
  IconTractor,
  IconTopologyStar3,
  IconTruckDelivery,
} from "@tabler/icons-react";
import { HOME_SECTOR_CARDS, type SectorCard } from "./sectorCatalog";

const sectorIconBySlug = {
  "energy-utilities": IconBolt,
  "industrial-manufacturing": IconBuildingFactory2,
  "mobility-aerospace-defence": IconPlaneTilt,
  "infrastructure-built-environment": IconTopologyStar3,
  "technology-electronics": IconSparkles,
  "environment-sustainability": IconLeaf,
  "agriculture-food-systems": IconTractor,
  "healthcare-life-sciences": IconHeartRateMonitor,
  "automotive-manufacturing-mobility-ecosystem": IconCar,
  "global-fmcg-supply-chain-distribution-ecosystem": IconTruckDelivery,
} as const;

const COMING_SOON_TEXT = "#334155";
const COMING_SOON_SURFACE = "#EDF2F7";
const COMING_SOON_CARD_BG = "#FFFFFF";
const COMING_SOON_CARD_BORDER = "#E2E8F0";
const COMING_SOON_CARD_PATTERN = `repeating-linear-gradient(
  135deg,
  ${COMING_SOON_CARD_BG} 0px,
  ${COMING_SOON_CARD_BG} 14px,
  #F1F5F9 14px,
  #F1F5F9 15px
)`;

export type SectorCardsGridProps = {
  /** Full grid on home/discover vs single column for discover sidebar filter. */
  variant?: "grid" | "sidebar";
  /** Called when user picks a sector (before navigation) — e.g. Discover moves cards to a left filter column. */
  onFilterEngaged?: () => void;
  /** When provided, sector/list clicks call this instead of navigating to `/discover?sector=`. */
  onSectorSelect?: (sector: SectorCard) => void;
};

function navigateToDiscoverSector(navigate: ReturnType<typeof useNavigate>, sector: SectorCard) {
  navigate({
    pathname: "/discover",
    search: `?sector=${encodeURIComponent(sector.slug)}`,
  });
}

export function SectorCardsGrid({
  variant = "grid",
  onFilterEngaged,
  onSectorSelect,
}: SectorCardsGridProps) {
  const navigate = useNavigate();
  const sortedSectorCards = useMemo(
    () => [...HOME_SECTOR_CARDS].sort((a, b) => b.subcategories.length - a.subcategories.length),
    [],
  );

  const engage = () => {
    onFilterEngaged?.();
  };

  const stopCardClick = (e: MouseEvent) => {
    e.stopPropagation();
  };

  const handleSectorSelect = (sector: SectorCard) => {
    if (sector.comingSoon) return;
    engage();
    if (onSectorSelect) {
      onSectorSelect(sector);
      return;
    }
    navigateToDiscoverSector(navigate, sector);
  };

  const gridCols = variant === "sidebar" ? 1 : { base: 1, sm: 2, lg: 4 };
  const cardPadding = variant === "sidebar" ? "sm" : "md";
  const titleSize = variant === "sidebar" ? 15 : 18;
  const iconSize = variant === "sidebar" ? 18 : 20;

  return (
    <SimpleGrid cols={gridCols} spacing={variant === "sidebar" ? "sm" : "md"}>
      {sortedSectorCards.map((sector) => {
        const SectorIcon = sectorIconBySlug[sector.slug as keyof typeof sectorIconBySlug] ?? IconSparkles;
        const isComingSoon = Boolean(sector.comingSoon);
        return (
          <Card
            key={sector.slug}
            withBorder
            radius="md"
            shadow={isComingSoon ? "none" : "xs"}
            p={cardPadding}
            style={{
              cursor: isComingSoon ? "default" : "pointer",
              ...(variant === "grid" && isComingSoon ? { gridColumn: "span 2" } : {}),
              ...(isComingSoon
                ? {
                    backgroundColor: COMING_SOON_CARD_BG,
                    backgroundImage: COMING_SOON_CARD_PATTERN,
                    borderColor: COMING_SOON_CARD_BORDER,
                  }
                : {}),
            }}
            onClick={isComingSoon ? undefined : () => handleSectorSelect(sector)}
          >
            <Stack gap={variant === "sidebar" ? "xs" : "sm"} h="100%">
              <Flex align="flex-start" gap={8}>
                {isComingSoon ? (
                  <Box
                    style={{
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: variant === "sidebar" ? 36 : 44,
                      height: variant === "sidebar" ? 36 : 44,
                      borderRadius: 8,
                      backgroundColor: COMING_SOON_SURFACE,
                    }}
                  >
                    <SectorIcon size={iconSize} color={COMING_SOON_TEXT} />
                  </Box>
                ) : (
                  <SectorIcon size={iconSize} color="#04305c" style={{ flexShrink: 0, marginTop: 2 }} />
                )}
                {isComingSoon ? (
                  <Flex
                    align="center"
                    justify="space-between"
                    gap="xs"
                    wrap="nowrap"
                    style={{ flex: 1, minWidth: 0 }}
                  >
                    <Title
                      order={4}
                      fz={titleSize}
                      lineClamp={variant === "sidebar" ? 2 : 2}
                      c={COMING_SOON_TEXT}
                      style={{ flex: 1, minWidth: 0 }}
                    >
                      {sector.title}
                    </Title>
                    <Text
                      size={variant === "sidebar" ? "xs" : "sm"}
                      fw={500}
                      c={COMING_SOON_TEXT}
                      px={10}
                      py={4}
                      style={{
                        flexShrink: 0,
                        borderRadius: 999,
                        backgroundColor: COMING_SOON_SURFACE,
                      }}
                    >
                      Coming Soon
                    </Text>
                  </Flex>
                ) : (
                  <Title order={4} fz={titleSize} lineClamp={variant === "sidebar" ? 2 : undefined}>
                    {sector.title}
                  </Title>
                )}
              </Flex>
              {!isComingSoon ? (
                <List size={variant === "sidebar" ? "xs" : "sm"} spacing={6}>
                  {sector.subcategories.map((item) => (
                    <List.Item
                      key={item}
                      style={{ cursor: "pointer", color: "#04305c", textDecoration: "underline" }}
                      onClick={(e) => {
                        stopCardClick(e);
                        handleSectorSelect(sector);
                      }}
                    >
                      {item}
                    </List.Item>
                  ))}
                </List>
              ) : null}
              {!isComingSoon ? (
                <Box mt="auto" onClick={stopCardClick}>
                  <Button
                    variant="light"
                    color="blue"
                    radius="md"
                    fullWidth
                    size={variant === "sidebar" ? "xs" : "sm"}
                    onClick={(e) => {
                      stopCardClick(e);
                      handleSectorSelect(sector);
                    }}
                    rightSection={<IconArrowRight size={14} />}
                  >
                    View Companies
                  </Button>
                </Box>
              ) : null}
            </Stack>
          </Card>
        );
      })}
    </SimpleGrid>
  );
}
